<script setup lang="ts">
import { clamp } from "lodash-es";
import { provide, ref, watch } from "vue";

import { keepBoardScrollPos } from "@/components/utils/boardRect";
import { useNativeEvents } from "@/composables/useNativeEvents";
import { adjustBoardLeftSize, adjustBoardRightSize } from "@/math/coordinate-utils";
import { useBoardSizeStore } from "@/store/boardSize";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useSidePanelStore } from "@/store/sidePanel";
import { windowSize } from "@/utils/dom/coordinates";

import { sidePanelKey } from "./injectKeys";

const MIN_WIDTH = 400;
// visible gap between board and panel when the board is scrolled to the end
const BOARD_GAP = 12;
const MAX_WIDTH = () => windowSize().x / 2;
const DEFAULT_WIDTH = 420;

const props = defineProps<{
  active: boolean;
  side: "left" | "right";
  resizable?: boolean;
  name?: string;
  // reserve scroll margin so the board can be scrolled clear of the panel
  // (always on for left panels, opt-in for right ones)
  pushBoard?: boolean;
}>();

// `explicit` distinguishes a user-initiated close (header/X button) from a
// programmatic one (eg. navigation, another panel opening).
const emit = defineEmits<{ close: [explicit: boolean] }>();
const userClose = () => emit("close", true);
const forceClose = () => emit("close", false);

// Called by the store when another panel takes over our side. Release the board
// margin as part of the eviction (the lock synchronously, the margin itself on
// the next tick): our `active` watcher only runs after the side changed hands,
// and the store then refuses our width-0 write.
const evict = () => {
  applyBoardMargin(0);
  forceClose();
};

provide(sidePanelKey, { close: userClose });

const boardSizeStore = useBoardSizeStore();

const width = ref(0);
const startX = ref(0);
const resizing = ref(false);

watch(
  () => props.active,
  (active) => setWidth(active ? initialWidth(props.name) : 0),
  { immediate: true },
);

function setWidth(w: number, resizing = false) {
  if (useSidePanelStore().setWidth(props.side, w, evict)) {
    width.value = w;
    if (!resizing) {
      applyBoardMargin(w);
      if (props.name && w !== 0) {
        useClientSettingsStore().setPanelWidth(props.name, w);
      }
    }
  }
}

// reserve (or give back, for w === 0) the scroll margin that keeps the board
// clear of this panel
function applyBoardMargin(w: number) {
  if (props.side === "left") {
    boardSizeStore.lockScrollableMargin("left", w > 0);
    keepBoardScrollPos(() => adjustBoardLeftSize(w));
  } else if (props.pushBoard) {
    boardSizeStore.lockScrollableMargin("right", w > 0);
    keepBoardScrollPos(() => adjustBoardRightSize(w > 0 ? w + BOARD_GAP : 0));
  }
}

function initialWidth(name?: string) {
  return useClientSettingsStore().panelWidth(name || "") || DEFAULT_WIDTH;
}

const { addEventListener, removeEventListeners } = useNativeEvents();

function startResize(event: PointerEvent) {
  resizing.value = true;
  event.stopPropagation();
  startX.value = event.pageX - resizeSign() * width.value;
  addEventListener(document, "pointermove", resize);
  addEventListener(document, "pointerup", endResize);
}

// A left panel widens when dragged right, a right panel when dragged left.
function resizeSign() {
  return props.side === "left" ? 1 : -1;
}

function endResize(event: PointerEvent) {
  resizing.value = false;
  removeEventListeners();
  resize(event, true);
}

function resize(event: PointerEvent, end = false) {
  setWidth(clamp(resizeSign() * (event.pageX - startX.value), MIN_WIDTH, MAX_WIDTH()), !end);
}
</script>

<template>
  <Transition :name="'side-panel-' + side">
    <div v-if="active" class="side-panel" :style="{ [side]: 0, width: width + 'px' }">
      <div class="content">
        <slot />
        <div v-if="resizing" class="capture" />
      </div>
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div v-if="resizable" class="resizer" :class="side" @pointerdown="startResize" />
    </div>
  </Transition>
</template>

<style scoped lang="scss">
@use "@/styles/variables" as vars;
@use "@/styles/variables/colors";
@use "@/styles/side-panel";

.side-panel {
  position: fixed;
  top: vars.$top-header-height;
  bottom: 0;
  background-color: colors.$background-ui;

  .content {
    width: 100%;
    height: 100%;

    // capture pointer events during resizing
    // (otherwise iframes can capture them and we might lose pointerup events)
    .capture {
      position: absolute;
      inset: 0;
    }
  }

  .resizer {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 10px;
    cursor: ew-resize;

    &.left {
      right: -5px;
    }

    &.right {
      left: -5px;
    }
  }
}

.side-panel-left-enter-active,
.side-panel-left-leave-active,
.side-panel-right-enter-active,
.side-panel-right-leave-active {
  transition:
    transform ease-in-out side-panel.$panel-transition,
    opacity ease-in-out side-panel.$panel-transition;
}

.side-panel-left-enter-from,
.side-panel-left-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.side-panel-right-enter-from,
.side-panel-right-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
